/**
 * SitePick front — Checkout 開始（docs/08 §1 / docs/10 §6）
 *
 * `.js-sp-checkout`（申し込む / 手続きを再開）クリックで `POST /checkout` に
 * `{ product_id }` だけを送る。価格・在庫はサーバが決める。成功で `begin_checkout` を
 * push してから Stripe の URL へトップレベル遷移する。失敗時は `checkout_error` を
 * 許可リストの reason で 1 回だけ push してから案内する（spec §3.2(c)）。
 * 取置き（24 時間）の holder は、サーバが `sp_hold` Cookie から取置きを解決する（送るのは product_id だけ）。
 */

import { announce } from "./live-region.js";
import { checkoutError, checkoutErrorReason, checkoutResume, checkoutReturn, push, readItem } from "./analytics.js";

/**
 * REST のベース URL（末尾スラッシュ付き）。
 *
 * @returns {string}
 */
function restBase() {
  const cfg = window.SITEPICK || {};
  const base = typeof cfg.restUrl === "string" && cfg.restUrl ? cfg.restUrl : "/wp-json/sitepick/v1/";
  return base.endsWith("/") ? base : `${base}/`;
}

/**
 * messages ヘルパ。
 *
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

/**
 * `begin_checkout` を送る。
 *
 * @param {Element} trigger
 * @param {string} [checkoutRef] サーバが返した結合キー（予約 ID の HMAC 先頭 16 桁。measurement-audit P1-3）
 * @returns {void}
 */
function fireBeginCheckout(trigger, checkoutRef) {
  const item = readItem(trigger);
  const value = item && typeof item.price === "number" ? item.price : 0;
  const currency = (item && item.currency) || (window.SITEPICK && window.SITEPICK.currency) || "JPY";
  const payload = {
    currency,
    value,
    items: item ? [item] : [],
  };
  if (typeof checkoutRef === "string" && /^[0-9a-f]{16}$/.test(checkoutRef)) {
    payload.checkout_ref = checkoutRef;
  }
  push("begin_checkout", payload);
}

/**
 * Stripe の画面から商品詳細へ戻ってきたことを 1 回だけ送る（`cancel_url` = 詳細 `?checkout=cancelled`。measurement.yaml `checkout_return`）。
 * 送ったら URL の印を消す（再読み込み・bfcache の復帰で二重に数えない）。item_id は詳細の `data-sp-item` から読む。
 *
 * @returns {void}
 */
function fireCheckoutReturn() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("checkout") !== "cancelled") {
      return;
    }
    const holder = document.querySelector(".js-sp-checkout") || document.querySelector("[data-sp-item]");
    const item = readItem(holder);
    checkoutReturn(item && item.item_id ? String(item.item_id) : "", "cancel");
    url.searchParams.delete("checkout");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {
    /* 計測の失敗で UI を止めない */
  }
}

/**
 * `checkout_error` を送る（1 回のクリックにつき 1 回。二重送信は `pending` ガードで起きない）。
 *
 * @param {Element} trigger
 * @param {number} status HTTP ステータス（fetch の例外なら 0）
 * @param {Object} [data] 応答 JSON
 * @returns {void}
 */
function fireCheckoutError(trigger, status, data) {
  const item = readItem(trigger);
  checkoutError(item && item.item_id ? String(item.item_id) : "", checkoutErrorReason(status, data));
}

/**
 * 画面に出ているパートナー文脈の種類（制度 ON のときだけテーマが `.js-sp-partner-note` を出す。OFF は ""）。
 * 紹介リンク（source = ref）の自動適用は、使えなくなってもサーバーが黙って外す（409 にしない）ので数えない。
 *
 * @returns {string} "referral" / "self" / ""
 */
function partnerKind() {
  const note = document.querySelector(".js-sp-partner-note");
  if (!(note instanceof HTMLElement) || note.dataset.spPartnerSource === "ref") {
    return "";
  }
  const kind = note.dataset.spPartnerKind || "";
  return kind === "referral" || kind === "self" ? kind : "";
}

/**
 * 紹介コードの割引率（テーマが注記に出したサーバーの設定値。無ければ承認済み仕様の 10）。
 *
 * @returns {number}
 */
function partnerBuyerPct() {
  const note = document.querySelector(".js-sp-partner-note");
  const pct = note instanceof HTMLElement ? Number(note.dataset.spPartnerBuyerPct || "") : NaN;
  return Number.isInteger(pct) && pct > 0 ? pct : 10;
}

let pending = false;

/**
 * クリックハンドラ。
 *
 * @param {MouseEvent} event
 * @returns {Promise<void>}
 */
async function onClick(event) {
  const trigger = event.target instanceof Element ? event.target.closest(".js-sp-checkout") : null;
  if (!trigger || trigger.hasAttribute("disabled") || trigger.classList.contains("is-disabled")) {
    return;
  }
  event.preventDefault();
  if (pending) {
    return;
  }

  const id = parseInt(trigger.getAttribute("data-sp-id") || "0", 10);
  if (!Number.isInteger(id) || id <= 0) {
    return;
  }

  const cfg = window.SITEPICK || {};
  pending = true;
  trigger.classList.add("is-loading");
  trigger.setAttribute("aria-busy", "true");
  announce(msg("checkoutStarting", "購入手続きを開始しています…"));

  // fetch 自体が例外になったときだけ reason=network。応答を受け取った後の例外（遷移失敗など）では
  // 送らない（応答の失敗分岐で送った後に二重にしない = 1 クリック 1 回）。
  let fetched = false;
  try {
    const res = await fetch(`${restBase()}checkout`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": typeof cfg.nonce === "string" ? cfg.nonce : "",
      },
      body: JSON.stringify({ product_id: id }),
    });
    fetched = true;

    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (res.ok && data && typeof data.url === "string" && data.url) {
      if (!data.resume && !data.complete) {
        fireBeginCheckout(trigger, data.checkout_ref);
      } else if (data.resume) {
        const item = readItem(trigger);
        checkoutResume(item && item.item_id ? String(item.item_id) : "", typeof data.checkout_ref === "string" ? data.checkout_ref : "");
      }
      window.location.assign(data.url);
      return;
    }

    fireCheckoutError(trigger, res.status, data);

    if (res.status === 429) {
      announce(msg("rateLimited", "アクセスが集中しています。しばらくして再度お試しください。"));
    } else if (res.status === 503 || (data && data.retryable)) {
      announce(msg("checkoutRetry", "お手数ですが、もう一度お試しください。"));
    } else if (res.status === 409 && data && data.code === "partner_self_unavailable") {
      // パートナー本人の購入（SELF）を開始できなかった（Stripe Customer を用意できない等）。サーバーは SELF を解除済み。
      announce(msg(
        "partnerSelfUnavailable",
        `パートナー価格を開始できませんでした。紹介コード（${partnerBuyerPct()}% OFF）はご利用いただけます。`
      ));
    } else if (res.status === 409 && data && data.code === "coupon_no_longer_valid" && partnerKind() === "referral") {
      // 手入力の紹介コードが使えなくなった（セール開始など。docs/21 §A.1）。サーバーは文脈を外し済みで、更新後は購入できる。
      announce(msg(
        "partnerNoLongerValid",
        "適用中の紹介コードはこの商品に使えなくなりました（セール対象）。ページを更新して価格をご確認ください。"
      ));
    } else if (res.status === 409 && data && data.code === "coupon_no_longer_valid" && partnerKind() === "self") {
      announce(msg(
        "partnerSelfNoLongerValid",
        "パートナー価格はこの商品に使えなくなりました。ページを更新して価格をご確認ください。"
      ));
    } else if (res.status === 409 && data && data.code === "coupon_no_longer_valid") {
      // 商品は買えるが、適用したクーポンが使えなくなった場合。
      // 「購入できません」と言うと誤解させるので、原因を分けて伝える
      // （通常価格で黙って進めない、という設計の説明でもある）。
      announce(msg(
        "couponNoLongerValid",
        "適用中のクーポンが使えなくなりました。ページを更新して、価格をご確認のうえもう一度お試しください。"
      ));
    } else if (res.status === 409 && data && data.code === "hold_expired") {
      // 取置き（24 時間）の期限切れ。holder の購入開始でだけ起きる。
      announce(msg("checkoutHoldExpired", "取置きの期限が切れました。ページを再読み込みしてください。"));
    } else if (res.status === 409 && data && data.code === "held_by_other") {
      announce(msg("checkoutHeldByOther", "現在、取置き中です"));
    } else if (res.status === 409) {
      announce(msg("checkoutUnavailable", "このサイトは現在購入できません。ページを更新してください。"));
    } else {
      announce((data && data.message) || msg("checkoutError", "購入手続きを開始できませんでした。時間をおいて再度お試しください。"));
    }
  } catch {
    if (!fetched) {
      fireCheckoutError(trigger, 0, null);
    }
    announce(msg("checkoutError", "購入手続きを開始できませんでした。時間をおいて再度お試しください。"));
  } finally {
    pending = false;
    trigger.classList.remove("is-loading");
    trigger.removeAttribute("aria-busy");
  }
}

/**
 * 初期化（委譲リスナ 1 本）。
 *
 * @returns {void}
 */
export function initCheckout() {
  // Stripe から戻ってきた印（?checkout=cancelled）は、申込みボタンの有無に関わらず 1 回だけ数えて消す。
  fireCheckoutReturn();
  // `[data-sp-hold-viewer]` … holder 本人と分かったら hold.js が後から申込みボタンを足す（取置き中の商品）。
  if (!document.querySelector(".js-sp-checkout, [data-sp-hold-viewer]")) {
    return;
  }
  document.addEventListener("click", onClick);
}
